<script>
  import { format } from 'svelte-i18n';

  import { SelectableList } from '../index.js';

  /**
   * Array of targeting items to display in the list.
   * @type {Array}
   * @default []
   */
  export let targets = [];

  /**
   * ID of the currently selected target.
   * @type {string | null}
   * @default null
   */
  export let selectedTargetId = null;

  /**
   * Callback function invoked when a target is selected.
   * @type {Function}
   * @default () => {}
   */
  export let onSelectTarget = () => {};

  /**
   * Type of targeting: 'group', 'domain', or 'recipient'.
   * @type {string}
   * @default 'group'
   */
  export let targetType = 'group';

  /**
   * Controls whether the loading spinner is displayed.
   * @type {boolean}
   * @default false
   */
  export let loading = false;

  const getTargetId = (target) => (targetType === 'group' ? target._id : target.name);
  const getEmptyMessage = () => {
    if (targetType === 'group') return $format('label.NO_GROUP_TARGETS_FOUND');
    if (targetType === 'domain') return $format('label.NO_DOMAINS_FOUND');
    return $format('label.NO_RECIPIENTS_FOUND');
  };
</script>

<SelectableList
  items={targets}
  selectedId={selectedTargetId}
  onSelect={onSelectTarget}
  emptyMessage={getEmptyMessage()}
  {loading}
  let:item
>
  {#if targetType === 'group'}
    <div class="route-content">
      <div class="priority-badge" class:active={item.status}>{item.priority}</div>

      <div class="route-section">
        <b>{$format('label.TARGET')}</b>
        {item.targettype === 'sender' ? $format('label.SENDER') : $format('label.RECIPIENT')}
      </div>

      <div class="route-section">
        <b>{$format('label.GROUPS')}</b>
        {#if item.sendergroups?.length}
          <div class="items">
            {#each item.sendergroups as group}
              <div class="item">{group.groupname}</div>
            {/each}
          </div>
        {:else}
          <div class="muted">{$format('label.NO_ASSIGNED_GROUPS')}</div>
        {/if}
      </div>

      <div class="route-section">
        <b>{$format('label.CAMPAIGNS')}</b>
        {#if item.ads?.length}
          <div class="items">
            {#each item.ads as campaign}
              <div class="item">{campaign.text}</div>
            {/each}
          </div>
        {:else}
          <div class="muted">{$format('label.NO_ASSIGNED_CAMPAIGNS')}</div>
        {/if}
      </div>

      <div class="route-section">
        <b>{$format('label.TAGS')}</b>
        {#if item.tags?.length}
          <div class="items">
            {#each item.tags as tag}
              <div class="item">{tag.text}</div>
            {/each}
          </div>
        {:else}
          <div class="muted">{$format('label.NO_ASSIGNED_TAGS')}</div>
        {/if}
      </div>
    </div>
  {:else}
    <div class="account-card-content">
      <div class="account-card-header">
        <div class="account-name">{item.name}</div>
      </div>

      {#if item.ads?.length}
        <div class="account-card-section">
          <b>{$format('label.CAMPAIGNS')}</b>
          <div class="items">
            {#each item.ads as campaign}
              <div class="item">{campaign.text}</div>
            {/each}
          </div>
        </div>
      {/if}

      {#if item.tags?.length}
        <div class="account-card-section">
          <b>{$format('label.TAGS')}</b>
          <div class="items">
            {#each item.tags as tag}
              <div class="item">{tag.text}</div>
            {/each}
          </div>
        </div>
      {/if}
    </div>
  {/if}
</SelectableList>

<style lang="scss">
  // Group target styles
  .route-content {
    align-items: center;
    display: grid;
    font-size: var(--font-size-medium);
    grid-template-columns: 60px repeat(4, minmax(min-content, 1fr));
    gap: 8px;
    margin: 0;
    padding: var(--padding-base) var(--padding-large);
    position: relative;

    .priority-badge {
      align-items: center;
      background-color: var(--color-gray-400);
      border-radius: var(--border-radius-base);
      color: var(--color-white);
      display: inline-flex;
      font-weight: var(--font-weight-medium);
      height: 30px;
      justify-content: center;
      padding: var(--padding-small) var(--padding-base);
      transition: all 0.3s ease-in-out;
      width: 30px;

      &.active {
        background-color: var(--color-blue-400);
      }
    }

    .route-section {
      > b {
        display: block;
        font-size: var(--font-size-medium);
        margin-bottom: var(--margin-base);
      }

      .items {
        align-items: center;
        display: flex;
        flex-wrap: wrap;
        gap: 4px;

        .item {
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius-base);
          color: var(--color-gray-700);
          display: inline-flex;
          font-size: var(--font-size-small);
          padding: var(--padding-small) var(--padding-base);
        }
      }

      .muted {
        color: var(--color-gray-600);
      }
    }
  }

  // Account/Domain/Recipient styles
  .account-card-content {
    display: grid;
    grid-template-columns: 1fr;
    padding: 0;
    position: relative;

    .account-card-header {
      border-bottom: 1px solid var(--border-color);
      padding: var(--padding-large);

      .account-name {
        font-weight: var(--font-weight-medium);
        font-size: var(--font-size-base);
        margin-bottom: 0;
      }
    }

    .account-card-section {
      padding: var(--padding-large);

      b {
        display: block;
        margin-bottom: var(--margin-base);
      }

      + .account-card-section {
        padding-top: 0;
      }

      .items {
        align-items: center;
        display: flex;
        flex-wrap: wrap;
        gap: 4px;

        .item {
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius-base);
          color: var(--color-gray-700);
          display: inline-flex;
          font-size: var(--font-size-small);
          padding: var(--padding-small) var(--padding-base);
        }
      }
    }
  }
</style>
